import { createStore } from 'vuex';

// http
import {
    addDeviceInfo,
    getAccount,
    updateResourceMap,
    getDeviceInfo,
    deleteDeviceInfo,
} from '../http/user';
import { getOrderEntities } from '../http/order';

const PENDING_ACTIVATION_ORDER = 2;

export default createStore({
    state: {
        accountData: null,
        scannedOntId: null,
        scannedRouterIds: [],
        userName: null,
        operator: null,
        orderData: null,
    },
    mutations: {
        setAccountData(state, accountData) {
            state.accountData = accountData;
        },
        setUserName(state, userName) {
            state.userName = userName;
        },
        setOperator(state, operator) {
            state.operator = operator;
        },
        setAccountPropertiesData(state, properties) {
            const previousProperties = state.accountData.properties;
            const newProperties = { ...previousProperties, ...properties };
            state.accountData.properties = newProperties;
        },
        setScannedOntId(state, id) {
            state.scannedOntId = id;
        },
        setScannedRouterId(state, ids) {
            state.scannedRouterIds = ids;
        },
        setOrderData(state, orderData) {
            state.orderData = orderData;
        },
    },
    actions: {
        setAccountData({ commit }, accountData) {
            commit('setAccountData', accountData.account_info);
            commit('setUserName', accountData.account_info?.user_owner_info ? `${accountData.account_info?.user_owner_info?.first_name} ${accountData.account_info?.user_owner_info?.last_name}` : '-');
            commit('setOperator', accountData.account_info?.properties ? accountData.account_info?.properties?.provider_id : null);
        },
        setScannedOntId({ commit }, id) {
            commit('setScannedOntId', id);
        },
        setScannedRouterId({ commit, state }, routerId) {
            const newRouterIds = [
                ...state.scannedRouterIds,
                routerId,
            ];
            commit('setScannedRouterId', newRouterIds);
        },
        setOrderData({ commit }, orderData) {
            commit('setOrderData', orderData?.order_data?.subscriber_id ? orderData : null);
        },
        removeScannedRouterId({ commit, state }, routerId) {
            commit('setScannedRouterId', state.scannedRouterIds.filter(id => routerId !== id));
        },
        resetOntAndRouterId({ commit }) {
            commit('setScannedOntId', null);
            commit('setScannedRouterId', []);
        },
        async updateResourceMap({ commit, state }, resources) {
            const resourcesForPayload = {};
            const removeDeviceInfo = [];
            let deviceInfo = [
                {
                    device_type: 'ont',
                    serial_number: resources.ontId,
                },
            ];
            if (resources.ontId) {
                resourcesForPayload.ont_id = resources.ontId;
                const existingOntId = state?.accountData?.properties?.ont_id;
                if (existingOntId && existingOntId !== resources.ontId) {
                    removeDeviceInfo.push({
                        deviceType: 'ont',
                        serialNumber: existingOntId,
                    });
                }
            }
            if (resources.routerIds?.length) {
                resourcesForPayload.router_ids = resources.routerIds;
                const currentRouterIds = state?.accountData?.properties?.router_ids || [];
                currentRouterIds.forEach(routerId => {
                    if (!resources.routerIds.includes(routerId)) {
                        removeDeviceInfo.push({
                            deviceType: 'router',
                            serialNumber: routerId,
                        });
                    }
                });
                deviceInfo = [
                    ...deviceInfo,
                    ...resources.routerIds.map(routerId => ({
                        device_type: 'router',
                        serial_number: routerId,
                    })),
                ];
            }
            if (resources.taskId) {
                resourcesForPayload.external_task_id = resources.taskId;
            }
            if (resources.serviceId) {
                resourcesForPayload.external_service_id = resources.serviceId;
            }

            const isPayload = !!Object.keys(resourcesForPayload).length;
            if (isPayload && deviceInfo?.length) {
                const subscriberId = state.orderData?.order_data?.subscriber_id;
                await addDeviceInfo(deviceInfo, subscriberId);
                const removeDeviceApiCalls = [];
                removeDeviceInfo.forEach(device => {
                    removeDeviceApiCalls.push(
                        deleteDeviceInfo(
                            subscriberId,
                            device.deviceType,
                            device.serialNumber,
                        ),
                    );
                });
                await Promise.all(removeDeviceApiCalls);
            }
            if (isPayload && resourcesForPayload?.external_service_id) {
                await updateResourceMap(resourcesForPayload.external_service_id, state.accountData.account_id);
            }
            commit('setAccountPropertiesData', resourcesForPayload);
        },
        async fetchAccountData({ commit, dispatch }, accountId) {
            const { data } = await getAccount(accountId);
            await dispatch('setAccountData', data);
            const response = await getOrderEntities(accountId);
            const order = Object.values(response.data.orders).find(o => o.state === PENDING_ACTIVATION_ORDER);
            await dispatch('setOrderData', order || null);
            const devicesInfo = await getDeviceInfo(order?.order_data?.subscriber_id);
            if (devicesInfo?.data?.device_info) {
                const { device_info: devices } = devicesInfo.data;
                const ontId = devices.find(device => device.device_type === 'ont')?.serial_number;
                const routerIds = devices.filter(device => device.device_type === 'router').map(device => device.serial_number);
                commit('setAccountPropertiesData', {
                    ont_id: ontId,
                    router_ids: routerIds,
                });
            }
        },
    },
    getters: {
        getScannedOntId: state => state.scannedOntId,
        getScannedRouterIds: state => state.scannedRouterIds,
        getUserName: state => state.userName,
        getOperator: state => state.operator,
        getChrIntegrationAccountId(state) {
            return state?.accountData?.properties?.chr_integration_account_id;
        },
        getChrAccountId(state) {
            return state?.accountData?.properties?.chr_account_id;
        },
        getServiceId(state) {
            return state?.accountData?.properties?.external_service_id;
        },
        getOntId(state) {
            return state?.accountData?.properties?.ont_id;
        },
        getRouterIds(state) {
            return state?.accountData?.properties?.router_ids || ['-'];
        },
        getExternalTaskId(state) {
            return state?.accountData?.properties?.external_task_id;
        },
        getAccountData(state) {
            return state?.accountData || {};
        },
        getOrderData(state) {
            return state?.orderData || null;
        },
    },
});
